import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material';
import { StorefrontOutlined, Info } from '@mui/icons-material';
import { Shop } from '../../types/shop.types';
import { WeeklyHours } from '../../types/common.types';

// Default hours: closed all days
const getDefaultHours = (): WeeklyHours => ({
  monday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  tuesday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  wednesday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  thursday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  friday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  saturday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
  sunday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
});

const shopSchema = z.object({
  name: z.string().min(2, 'Shop name must be at least 2 characters'),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
});

type ShopFormValues = z.infer<typeof shopSchema>;

interface ShopFormProps {
  open: boolean;
  shop?: Shop | null;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    address?: string;
    contactInfo?: { phone?: string; email?: string };
    defaultHours: WeeklyHours;
  }) => Promise<void>;
  isLoading?: boolean;
}

export default function ShopForm({ open, shop, onClose, onSubmit, isLoading }: ShopFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ShopFormValues>({
    resolver: zodResolver(shopSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      email: '',
    },
  });

  // Reset form when shop changes or dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: shop?.name || '',
        address: shop?.address || '',
        phone: shop?.contactInfo?.phone || '',
        email: shop?.contactInfo?.email || '',
      });
    }
  }, [open, shop, reset]);

  const handleFormSubmit = async (data: ShopFormValues) => {
    try {
      await onSubmit({
        name: data.name,
        address: data.address,
        contactInfo: {
          phone: data.phone,
          email: data.email,
        },
        defaultHours: shop?.defaultHours || getDefaultHours(),
      });
      handleClose();
    } catch (error) {
      console.error('Form submit error:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <StorefrontOutlined color="primary" />
            <Typography variant="h5" fontWeight={600}>
              {shop ? 'Edit Shop' : 'Create New Shop'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {shop
              ? 'Update shop details and contact information'
              : 'Add a new shop location to your organization'}
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 1,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                >
                  SHOP INFORMATION
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="error.main"
                  sx={{ ml: 'auto' }}
                >
                  * Required
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField
                    {...register('name')}
                    label="Shop Name"
                    fullWidth
                    autoFocus
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message || 'Required field'}
                    disabled={isLoading}
                    placeholder="e.g., Downtown Store"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    {...register('address')}
                    label="Address"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.address}
                    helperText={errors.address?.message || 'Optional'}
                    disabled={isLoading}
                    placeholder="Enter street address, city, state, and ZIP"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 1,
                },
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                CONTACT INFORMATION
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('phone')}
                    label="Phone Number"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message || 'Optional'}
                    disabled={isLoading}
                    placeholder="(555) 123-4567"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('email')}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message || 'Optional'}
                    disabled={isLoading}
                    placeholder="shop@example.com"
                  />
                </Grid>
              </Grid>
            </Paper>

            {!shop && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'info.50',
                  border: '1px solid',
                  borderColor: 'info.200',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Info color="info" fontSize="small" />
                <Box>
                  <Typography variant="body2" fontWeight={500} color="info.main">
                    Next Steps
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    After creating the shop, you can configure its operating hours and
                    coverage requirements.
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isLoading} size="large">
            Cancel
          </Button>
          <Tooltip
            title={
              !isValid && !isLoading
                ? 'Please fill in all required fields to continue'
                : ''
            }
            arrow
          >
            <span>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !isValid}
                size="large"
                sx={{ minWidth: 120 }}
              >
                {isLoading
                  ? 'Saving...'
                  : shop
                  ? 'Update Shop'
                  : 'Create Shop'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </form>
    </Dialog>
  );
}
